"use client";

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Building2,
  Crown,
  Megaphone,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  IndianRupee,
  ShoppingCart,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type AdminSummary = {
  companies_total: number;
  companies_active: number;
  users_total: number;
  sales_total: number;
  sales_net_amount: number;
  leads_total?: number;
  leads_converted?: number;
};

type StaffSummary = {
  my_leads_total: number;
  my_leads_converted: number;
};

type Marketing = {
  offers_total: number;
  offers_active_now: number;
};

type CompanyRow = {
  id: number;
  name: string;
  gstin?: string | null;
  phone?: string | null;
  email?: string | null;
  is_active?: number;
  created_at?: string | null;
  users_count?: number | string;
  sales_count?: number | string;
};

type LeadRow = {
  id: number;
  company_name: string;
  status: string;
  email?: string | null;
  phone?: string | null;
  created_at?: string | null;
};

type DashboardPayload =
  | {
      role: "product_admin";
      summary: AdminSummary;
      marketing: Marketing;
      top_companies: CompanyRow[];
    }
  | {
      role: "product_admin_staff";
      summary: StaffSummary;
      recent_leads: LeadRow[];
    };

const adminStats: { key: string; label: string; field: keyof AdminSummary; icon: LucideIcon; format?: "money" }[] = [
  { key: "companies", label: "Companies", field: "companies_total", icon: Building2 },
  { key: "active", label: "Active", field: "companies_active", icon: Sparkles },
  { key: "users", label: "Users", field: "users_total", icon: Users },
  { key: "sales", label: "Sales", field: "sales_total", icon: ShoppingCart },
  { key: "net", label: "Net amount", field: "sales_net_amount", icon: IndianRupee, format: "money" },
];

export default function ProductAdminDashboardPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["product-admin", "dashboard"],
    queryFn: async () => {
      const { data } = await api.get<DashboardPayload>("/api/product-admin/dashboard");
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center bg-sky-50/60 p-6 text-sm text-slate-500">
        Loading dashboard…
      </div>
    );
  }
  if (isError || !data) {
    return <p className="bg-sky-50/60 p-6 text-sm text-destructive">Could not load dashboard.</p>;
  }

  if (data.role === "product_admin_staff") {
    return (
      <div className="min-h-full space-y-6 bg-sky-50/60 p-6">
        <div className="rounded-2xl border border-sky-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                <Target className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">My dashboard</h1>
                <p className="text-sm text-slate-500">Your leads and pipeline at a glance.</p>
              </div>
            </div>
            <Link to="/product-admin/leads" className={buttonVariants({ variant: "default", size: "sm" })}>
              + Add lead
            </Link>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">My leads</p>
              <p className="mt-1 text-3xl font-bold tabular-nums text-sky-800">
                {data.summary.my_leads_total}
              </p>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Converted</p>
              <p className="mt-1 text-3xl font-bold tabular-nums text-sky-700">
                {data.summary.my_leads_converted}
              </p>
            </div>
          </div>
        </div>

        <Card className="border-sky-200 bg-white shadow-sm">
          <CardHeader className="border-b border-sky-100 bg-sky-50/60">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-slate-900">Recent leads</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto rounded-lg border border-sky-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-sky-100 bg-sky-50/60 text-left text-slate-600">
                    <th className="py-2.5 pl-3 pr-3">ID</th>
                    <th className="py-2.5 pr-3">Company</th>
                    <th className="py-2.5 pr-3">Email</th>
                    <th className="py-2.5 pr-3">Phone</th>
                    <th className="py-2.5 pr-3">Status</th>
                    <th className="py-2.5 pr-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_leads.map((l, i) => (
                    <tr
                      key={l.id}
                      className={cn(
                        "border-b border-sky-50 last:border-0 transition-colors hover:bg-sky-50/40",
                        i % 2 === 1 && "bg-sky-50/30",
                      )}
                    >
                      <td className="py-2.5 pl-3 pr-3 font-mono text-xs text-slate-600">{l.id}</td>
                      <td className="py-2.5 pr-3 font-medium text-slate-800">{l.company_name}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{l.email ?? ""}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{l.phone ?? ""}</td>
                      <td className="py-2.5 pr-3">
                        <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs capitalize text-sky-700">
                          {l.status}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-500">{l.created_at ?? ""}</td>
                    </tr>
                  ))}
                  {data.recent_leads.length === 0 ? (
                    <tr>
                      <td className="py-8 text-center text-slate-500" colSpan={6}>
                        No leads yet. Click “Add lead”.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="min-h-full space-y-6 bg-sky-50/60 p-6">
      <div className="rounded-2xl border border-sky-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
            <Crown className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Product admin</h1>
            <p className="text-sm text-slate-500">Companies, revenue footprint, and marketing signals.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {adminStats.map((t) => {
          const Icon = t.icon;
          const raw = s[t.field];
          const display = t.format === "money" ? `₹${Number(raw ?? 0).toFixed(2)}` : String(raw ?? 0);

          return (
            <Card
              key={t.key}
              className="border-sky-200 bg-white shadow-sm transition-shadow hover:shadow-md"
            >
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t.label}
                </CardTitle>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                  <Icon className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums tracking-tight text-slate-900">{display}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-sky-200 bg-white shadow-sm lg:col-span-1">
          <CardHeader className="border-b border-sky-100 bg-sky-50/60">
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-slate-900">Marketing</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pt-4 text-sm">
            <Row label="Offers (total)" value={data.marketing.offers_total} />
            <Row label="Offers (active now)" value={data.marketing.offers_active_now} highlight />
            <Row label="Leads (total)" value={Number(s.leads_total ?? 0)} />
            <Row label="Leads converted" value={Number(s.leads_converted ?? 0)} highlight />
          </CardContent>
        </Card>

        <Card className="border-sky-200 bg-white shadow-sm lg:col-span-2">
          <CardHeader className="border-b border-sky-100 bg-sky-50/60">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-slate-900">Top companies (by sales)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto rounded-lg border border-sky-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-sky-100 bg-sky-50/60 text-left text-slate-600">
                    <th className="py-2.5 pl-3 pr-3">ID</th>
                    <th className="py-2.5 pr-3">Company</th>
                    <th className="py-2.5 pr-3">Email</th>
                    <th className="py-2.5 pr-3">Phone</th>
                    <th className="py-2.5 pr-3">Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_companies.map((c, i) => (
                    <tr
                      key={c.id}
                      className={cn(
                        "border-b border-sky-50 last:border-0 transition-colors hover:bg-sky-50/40",
                        i % 2 === 1 && "bg-sky-50/30",
                      )}
                    >
                      <td className="py-2.5 pl-3 pr-3 font-mono text-xs text-slate-600">{c.id}</td>
                      <td className="py-2.5 pr-3 font-medium text-slate-800">{c.name}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{c.email ?? ""}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{c.phone ?? ""}</td>
                      <td className="py-2.5 pr-3">
                        <span className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 font-medium tabular-nums text-sky-800">
                          {Number(c.sales_count ?? 0)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-sky-100 bg-sky-50/40 px-3 py-2">
      <span className="text-slate-600">{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums text-slate-800",
          highlight && "text-sky-800",
        )}
      >
        {value}
      </span>
    </div>
  );
}
